/**
 * PanelManager.js
 * 
 * This module provides the PanelManager class that handles all operations related to
 * solar panel placement, creation, and interaction within the Eagle Eye Designer application.
 * 
 * The PanelManager handles:
 * 1. Creating solar panel meshes based on selected panel specifications
 * 2. Handling mouse interactions for panel placement
 * 3. Managing panel positioning, rotation, and validation
 * 4. Creating visual feedback for valid/invalid placement
 * 5. Managing grid snapping with other panels
 * 6. Adding placed panels to the application state
 */

import * as THREE from 'three';
import { createSegmentPlane } from "../Global/functions";
import { POLYGONS } from "../Global/variables";
import { gridHelper, panelInSegment } from './Placement';
import { createWireframe, getEdgeDirection, getRotation } from './Helpers';
import { COLORS } from '../Global/materials';
import { checkPanelsOK } from './PanelFunctions';
import { degToRad } from 'three/src/math/MathUtils.js';

/**
 * Class to manage the creation, positioning, and interaction of solar panels
 */
class PanelManager {
  /**
   * Constructor for the PanelManager class
   * 
   * @param {THREE.Scene} scene - The THREE.js scene where panels will be rendered
   * @param {THREE.Camera} camera - The camera for raycasting and mouse interactions
   * @param {number} segmentIndex - Index of the current roof segment in the POLYGONS array
   * @param {Object} solarPanel - Object containing solar panel specifications
   * @param {Function} updatePanelList - Callback function to update the panel list in state
   * @param {Array} panelList - Array of available panel types/models
   * @param {THREE.Mesh|null} panel - The current panel mesh being placed (null if none yet)
   * @param {THREE.Vector3} position - Initial position for panel placement
   * @param {boolean} selected - Whether the panel is currently selected
   */
  constructor(scene, camera, segmentIndex, solarPanel, updatePanelList, panelList, panel, position, selected) {
    this.scene = scene;
    this.camera = camera;
    this.segmentIndex = segmentIndex;
    this.solarPanel = solarPanel;
    this.updatePanelList = updatePanelList;
    this.panelList = panelList;
    this.panel = panel;
    this.position = position;
    this.selected = selected;
    this.area = 0;              // Panel area in square feet
    this.offset = 0;            // Height offset for the panel (stored in inches, for UI)
    this.plane = null;          // Segment plane for intersection calculations
    this.grid = null;           // Grid helper for panel alignment
    this.first = null;          // Reference to the first panel for grid positioning
  }

  /**
   * Handles mouse down events during panel placement
   * 
   * When the user clicks to place a panel, this checks if the placement is valid
   * and adds the panel to the segment if so.
   * 
   * @param {Function} setPanelsOK - Callback to update panel validity state
   */
  handleMouseDown(setPanelsOK) {
    // Check if the panel is validly placed within the segment
    if (panelInSegment(this.panel, this.segmentIndex)) {
      // Hide the panel outline and set its color to red (will be invisible due to opacity 0)
      this.panel.children[0].material.setValues({ opacity: 0, color: COLORS.red });
      // Add the panel to the segment
      this.addPanel();
    }
  }

  /**
   * Handles mouse move events during panel placement
   * 
   * Updates the panel position as the mouse moves and handles grid snapping.
   * 
   * @param {THREE.Vector2} mouse - Normalized mouse coordinates
   * @returns {Promise} A promise that resolves when the operation is complete
   */
  async handleMouseMove(mouse) {
    // Calculate where the panel should be based on mouse position
    await this.getPlaneIntersect(mouse);
  }

  /**
   * Handles mouse up events during panel placement
   * 
   * When the user releases the mouse button, this validates the final panel position.
   * If invalid, it resets the panel to its original position.
   * 
   * @param {Function} setPanelsOK - Callback to update panel validity state
   * @param {boolean} arePanelsOK - Current panel validity state
   */
  handleMouseUp(setPanelsOK, arePanelsOK) {
    // If the panel is not validly placed, reset it to the original position
    if (!panelInSegment(this.panel, this.segmentIndex)) {
      this.panel.position.copy(this.position);
    }
    // Check if all panels are validly placed after this change
    checkPanelsOK(this.segmentIndex, setPanelsOK, arePanelsOK);
  }

  /**
   * Calculates the intersection point between the mouse ray and the segment plane
   * 
   * This function is used to determine where the panel should be positioned
   * based on the current mouse position.
   * 
   * @param {THREE.Vector2} mouse - Normalized mouse coordinates
   * @returns {Promise} A promise that resolves when the operation is complete
   */
  async getPlaneIntersect(mouse) {
    // Create a raycaster and update it with the camera and mouse coordinates
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, this.camera);

    // Create the plane representing the segment surface
    this.plane = createSegmentPlane(this.segmentIndex);
    const segment = POLYGONS[this.segmentIndex].shape;

    // Calculate where the ray from the mouse intersects the segment plane
    const intersection = new THREE.Vector3();
    raycaster.ray.intersectPlane(this.plane, intersection);

    // If no panel exists yet, create one at the intersection point
    if (!this.panel) {
      let _ = await this.createPanel(intersection, POLYGONS[this.segmentIndex], this.plane);
    } else {
      // If panel already exists, update its position
      const orientation = this.plane.normal.clone().normalize();

      // Ensure the orientation vector points upward
      if (orientation.z < 0) {
        orientation.negate();
      }

      this.updatePanelPosition(intersection, orientation);
    }
  }

  /**
   * Creates a new solar panel mesh at the specified position
   * 
   * This function loads the panel texture, creates the mesh geometry,
   * and positions the panel correctly on the segment plane.
   * 
   * @param {THREE.Vector3} mousePosition - Position where the panel should be created
   * @param {THREE.Mesh} segment - The segment mesh
   * @param {THREE.Plane} plane - The segment plane
   * @returns {Promise} A promise that resolves when the panel is created
   */
  createPanel(mousePosition, segment, plane, isLoaded = false) {
  return new Promise((resolve, reject) => {
    this.plane = plane;
    let w, width = 0;
    let h, height = 0;

    // Find the panel data from the available panel list
    const data = this.panelList.filter((panel) => panel.id === this.solarPanel.id)[0];
    
    // Convert panel dimensions based on the unit system (Metric or Imperial)
    if (data.unit === 'Metric') {
      // Convert millimeters or centimeters to meters
      if (data.width_unit === 'mm') w = data.width / 1000;
      else w = data.width / 100;
      if (data.height_unit === 'mm') h = data.height / 1000;
      else h = data.height / 100;
    }
    else if (data.unit === 'Imperial') {
      // Convert inches to meters
      w = data.width / 39.37;
      h = data.height / 39.37;
    }

    // Calculate area in square feet
    this.area = w * h * 10.764;
    
    // Set dimensions based on panel orientation (portrait or landscape)
    if (this.solarPanel.orientation === "Portrait") {
      width = w;
      height = h;
    } else {
      width = h;
      height = w;
    }

    // Create panel geometry based on dimensions
    const geometry = new THREE.PlaneGeometry(width, height);
    
    // Get normal vectors for orientation
    const normal = new THREE.Vector3(0, 0, 1); // Up vector for default plane orientation
    const orientation = this.plane.normal.clone().normalize();
    
    // Ensure the orientation vector points upward (same idea as auto-populate's "outward")
    if (orientation.z < 0) {
      orientation.negate();
    }

    // Get direction along the segment edge for proper panel alignment
    const direction = getEdgeDirection(segment, orientation);
    const max = new THREE.Vector3(orientation.x, orientation.y, 0).normalize();
    
    // Fixed small clearance above the roof plane (meters),
    // plus optional solarPanel.height (treated as inches).
    const clearanceOffsetM = 0.01; // 1 cm clearance
    const baseHeightM = (this.solarPanel?.height || 0) / 39.37; // if height is in inches
    const totalOffsetM = clearanceOffsetM + baseHeightM;

    // Store offset in inches for UI/state if needed
    this.offset = totalOffsetM * 39.37;

    // Load the panel texture
    const textureLoader = new THREE.TextureLoader();
    textureLoader.load(
      sessionStorage.getItem(data.id.toString()), // Get texture URL from session storage
      (texture) => {
        // Set texture wrapping and repeat settings
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(1, 1); // No tiling

        // Rotate texture if panel is in landscape orientation
        if (this.solarPanel.orientation === "Landscape") {
          texture.rotation = Math.PI / 2;
        }

        // Create the panel mesh if it doesn't exist yet
        if (!this.panel) {
          // Create material with the loaded texture
          const material = new THREE.MeshStandardMaterial({ 
            map: texture, 
            roughness: 0.8, 
            side: THREE.DoubleSide, 
            depthTest: false, 
            depthWrite: false, 
            transparent: true 
          });
          
          // Create the panel mesh
          this.panel = new THREE.Mesh(geometry, material);
          this.panel.name = "panel";
          this.panel.renderOrder = 5; // High render order to ensure visibility
          this.panel.frustumCulled = false; // Always render, even if outside camera frustum
          
          // Set panel type property for grid snapping calculations
          if (this.solarPanel.orientation === "Portrait"){
            this.panel.panelType = 'potrait';
          } else {
            this.panel.panelType = 'landscape';
          }

          // Calculate rotation quaternion
          let quaternion = new THREE.Quaternion();
          
          // Apply tilt if specified
          if (this.solarPanel.tilt) {
            const temp = new THREE.Quaternion().setFromUnitVectors(normal, max);  
            quaternion.rotateTowards(temp, degToRad(Number(this.solarPanel.tilt)));      
          } else {
            // Otherwise, align with the segment plane
            quaternion.setFromUnitVectors(normal, orientation);
          }
          
          // Apply the calculated rotation
          this.panel.applyQuaternion(quaternion);
          
          // Get additional rotation to align with segment edges
          const [sign, rotation] = getRotation(orientation, direction, quaternion);
          this.panel.applyQuaternion(rotation);
          
          // ✅ Decide final position:
          // - Interactive/new placement: ray hit on plane + totalOffsetM
          // - Loaded from saved data: saved world position already has offset → use as-is
          let finalPosition;
          if (isLoaded) {
            finalPosition = mousePosition.clone(); // already full world position
          } else {
            finalPosition = mousePosition.clone().add(
              orientation.clone().multiplyScalar(totalOffsetM)
            );
          }

          this.panel.position.copy(finalPosition);
          
          // Create wireframe outline for visual feedback
          createWireframe(this.panel);
          
          // Add the panel to the scene
          this.scene.add(this.panel);
          resolve();
        } else {
          // If panel already exists, just update its position
          this.updatePanelPosition(mousePosition, orientation);
        }
      },
      undefined,
      (error) => {
        console.error('Error loading texture:', error);
        reject();
      }
    );
  });
}


  /**
   * Updates the panel position and handles visual feedback and grid snapping
   * 
   * This function is called as the mouse moves to update where the panel appears.
   * It also handles visual feedback for valid/invalid placement and grid snapping.
   * 
   * @param {THREE.Vector3} mousePosition - Current mouse position in 3D space
   * @param {THREE.Vector3} orientation - Normal vector of the segment plane
   */
  updatePanelPosition(mousePosition, orientation) {
    // ✅ Keep manual placement consistent with createPanel / autoPopulate:
    // a fixed clearance above the plane + optional solarPanel.height.
    const clearanceOffsetM = 0.01; // 1 cm
    const baseHeightM = (this.solarPanel?.height || 0) / 39.37;
    const totalOffsetM = clearanceOffsetM + baseHeightM;

    // Store (for UI/state if needed)
    this.offset = totalOffsetM * 39.37;

    // Set the panel position based on mouse position plus offset
    this.panel.position.copy(
      mousePosition.clone().add(
        orientation.clone().multiplyScalar(totalOffsetM)
      )
    );
    
    // Update visual feedback based on placement validity
    if (!panelInSegment(this.panel, this.segmentIndex)) {
      // Red outline for invalid placement
      this.panel.children[0].material.setValues({ opacity: 1, color: COLORS.red });
    }
    else {
      if (this.selected) {
        // Yellow outline for selected valid panels
        this.panel.children[0].material.setValues({ opacity: 1, color: COLORS.yellow });
      } else {
        // No outline for valid unselected panels
        this.panel.children[0].material.setValues({ opacity: 0 });
      }
    }
    
    // Handle grid snapping if there are other panels in the segment
    if (POLYGONS[this.segmentIndex].panels.length > 0) {
      // Remove previous grid if it exists
      if (this.grid) {
        this.first.remove(this.grid);
        this.grid.geometry.dispose();
        this.grid.material.dispose();
      }
      
      // Calculate new grid and snapped position
      let position = null;
      [this.grid, position, this.first] = gridHelper(this.panel, POLYGONS[this.segmentIndex].panels);
      
      // Add the new grid if it exists
      if (this.grid) {
        this.first.add(this.grid);
      }
      
      // Update panel position to the snapped position
      if (position) {
        this.panel.position.copy(position);
      }
    }
  }

  /**
   * Adds the current panel to the segment and updates the UI
   * 
   * This function is called when a panel is successfully placed.
   * It adds the panel to the POLYGONS data structure and updates the UI.
   */
  addPanel() {
    // Add panel to the segment's panels array with its area
    POLYGONS[this.segmentIndex].panels.push({panel: this.panel, area: this.area, id: this.solarPanel.id});
    
    // Clean up the grid helper if it exists
    if (this.grid) {
      this.first.remove(this.grid);
      this.grid.geometry.dispose();
      this.grid.material.dispose();
      this.grid = null;
    }
    
    // Prepare panel data for the UI
    let uiData = { 
      ...this.solarPanel, 
      panel_id: this.panel.id, 
      offset: this.offset 
    };
    
    // Update the panel list in the UI
    this.updatePanelList({ 
      newPanel: uiData, 
      segmentIndex: this.segmentIndex 
    });
    
    // Reset state to prepare for next panel placement
    this.panel = null;
    this.offset = 0;
  }
}

export default PanelManager;
